import { Component, Mixins, Prop } from 'vue-property-decorator'
import { mapActions, mapGetters, mapState } from 'vuex'
import { RawLocation } from 'vue-router'
import { KTable } from '@kwixeo/table-vue'
import Table from '@kwixeo/table-vue/dist/components/Table/Table'
import { emitter, NumberMixin, Spinner } from '@kwixeo/interface-vue'
import ManualMovementItem from '../ManualMovementItem/ManualMovementItem.vue'
import LotItem from '../LotItem/LotItem.vue'

@Component({
  methods: {
    ...mapActions('dynamicModals', ['openModal']),
    ...mapActions('catalog', ['loadStockLocations'])
  },
  computed: {
    ...mapGetters('subscriptions', ['hasModule']),
    ...mapState('catalog', ['stockLocations'])
  },
  components: {
    KTable,
    ManualMovementItem,
    LotItem,
    Spinner
  }
})
export default class MovementList extends Mixins(NumberMixin) {
  openModal!: (param: KwixeoInterface.OpenModalParam) => void
  hasModule!: (key: string) => boolean
  loadStockLocations!: (force?: boolean) => Promise<void>
  stockLocations!: KwixeoCatalog.StockLocations

  @Prop({ default: null }) productId!: number|null

  private loading = false
  private manualMovementItem: KwixeoCatalog.StockManualMovement|null = null
  private lotItem: KwixeoCatalog.ProductLot|null = null

  async mounted (): Promise<void> {
    if (this.hasModule('stock-locations')) {
      this.loading = true
      await this.loadStockLocations()
      this.loading = false
    }
    emitter.on('productCreated', this.productCreated)
    emitter.on('productUpdated', this.productUpdated)
  }

  destroyed (): void {
    emitter.off('productCreated', this.productCreated)
    emitter.off('productUpdated', this.productUpdated)
  }

  get stateKey (): string {
    return this.productId ? 'stock-movements-product' : 'stock-movements'
  }

  get ajaxDatas (): KwixeoTable.Object {
    const filter: KwixeoInterface.GenericObject = {}
    if (this.productId) {
      filter.product = this.productId
    }
    return {
      filter,
      include: 'element,product,variation,stock_location'
    }
  }

  private rowClick (row: KwixeoCatalog.StockMovement): void|null {
    if (!row.element) {
      return null
    }

    switch (row.element_type) {
      case 'stockManualContent':
        this.manualMovementItem = row.element as KwixeoCatalog.StockManualMovement
        break
      case 'productLot':
        this.lotItem = row.element as KwixeoCatalog.ProductLot
        break
      default:
        return null
    }
  }

  private rowLink (row: KwixeoCatalog.StockMovement): RawLocation|null {
    if (['product', 'productLot'].includes(row.element_type) || !row.element) {
      return null
    }

    switch (row.element_type) {
      case 'providerCommandContent':
        return { name: 'stock-command-item', params: { id: String(row.element.id) }}
      case 'quotationContent':
        return { name: 'quotation-item', params: { id: String(row.element.id) }}
      case 'commandContent':
        return { name: 'command-item', params: { id: String(row.element.id) }}
      case 'deliveryContent':
        return { name: 'delivery-item', params: { id: String(row.element.id) }}
      case 'invoiceContent':
        return { name: 'invoice-item', params: { id: String(row.element.id) }}
      case 'creditContent':
        return { name: 'credit-item', params: { id: String(row.element.id) }}
      default:
        return null
    }
  }

  get columns (): KwixeoTable.Columns {
    const columns: KwixeoTable.Columns = [
      {
        data: 'created_at',
        name: 'Date / heure',
        render: 'datetime'
      },
      {
        data: 'product.reference',
        name: 'Référence produit',
        visible: this.productId === null
      },
      {
        data: 'product.name',
        name: 'Nom produit',
        visible: this.productId === null
      },
      {
        data: 'variation.reference',
        name: 'Référence variation',
        visible: false
      },
      {
        data: 'quantity',
        name: 'Quantité',
        render: (val: string) => this.showNumber(val)
      },
      {
        data: 'element_type',
        name: 'Origine du mouvement',
        sortable: false,
        isHtml: true,
        render: (element_type: string, movement: KwixeoCatalog.StockMovement) => {
          const element = movement.element

          switch (element_type) {
            case 'stockManualContent':
              if (!element) {
                return '<i>' + this.$t('catalog.stock.types.stockManualContentDelete') + '</i>'
              } else if ('type' in element && element.type === 1) {
                return this.$t('catalog.stock.types.stockManualContentEntry', { reference: element.reference })
              } else {
                return this.$t('catalog.stock.types.stockManualContentOuput', { reference: element.reference })
              }
            case 'providerCommandContent':
              if (element && 'reference' in element) {
                return this.$t('catalog.stock.types.providerCommand', { reference: element.reference })
              }
              return '<i>' + this.$t('catalog.stock.types.providerCommandDelete') + '</i>'
            case 'product':
            case 'productStock':
              return this.$t('catalog.stock.types.' + (movement.product?.is_group ? 'group' : 'product'))
            case 'productVariation':
              return this.$t('catalog.stock.types.productVariation')
            case 'productLot':
              return this.$t('catalog.stock.types.productLot')
            case 'quotationContent':
              if (element && 'reference' in element) {
                return this.$t('catalog.stock.types.quotation', { reference: element.reference })
              }
              return '<i>' + this.$t('catalog.stock.types.quotationDelete') + '</i>'
            case 'commandContent':
              if (element && 'reference' in element) {
                return this.$t('catalog.stock.types.command', { reference: element.reference })
              }
              return '<i>' + this.$t('catalog.stock.types.commandDelete') + '</i>'
            case 'proformaContent':
              if (element && 'reference' in element) {
                return this.$t('catalog.stock.types.proforma', { reference: element.reference })
              }
              return '<i>' + this.$t('catalog.stock.types.proformaDelete') + '</i>'
            case 'deliveryContent':
              if (element && 'reference' in element) {
                return this.$t('catalog.stock.types.delivery', { reference: element.reference })
              }
              return '<i>Bon de livraison supprimé</i>'
            case 'invoiceContent':
              if (element && 'reference' in element) {
                return this.$t('catalog.stock.types.invoice', { reference: element.reference })
              }
              return '<i>' + this.$t('catalog.stock.types.invoiceDelete') + '</i>'
            case 'creditContent':
              if (element && 'reference' in element) {
                return this.$t('catalog.stock.types.credit', { reference: element.reference })
              }
              return '<i>' + this.$t('catalog.stock.types.creditDelete') + '</i>'
            default:
              return null
          }
        }
      }
    ]


    if (this.hasModule('stock-locations')) {
      columns.push({
        data: 'stock_location.name',
        name: 'Lieu de stock'
      })
    }

    columns.push({
      data: 'element_type',
      name: 'Commentaires',
      isHtml: true,
      sortable: false,
      visible: false,
      render: (element_type: string, movement: KwixeoCatalog.StockMovement) => {
        const element = movement.element
        if (element && 'title' in element) {
          return element.title
        } else if (element && 'comments' in element) {
          return element.comments
        } else {
          return null
        }
      }
    })

    return columns
  }

  get filters (): KwixeoTable.FiltersOption {
    const filters: KwixeoTable.FiltersOption = [
      {
        type: 'select',
        name: 'theoretical',
        label: 'Type de mouvement',
        defaultValue: { label: 'Réel', value: false },
        options: [
          { label: 'Réel', value: false },
          { label: 'Théorique', value: true }
        ],
        optionLabel: 'label',
        value: 'value',
        params: {
          clearable: false
        }
      },
      {
        type: 'period',
        label: 'Date du mouvement',
        name: 'created_since',
        name2: 'created_until'
      },
      {
        type: 'select',
        label: 'Produit',
        name: 'product',
        multiple: true,
        ajax: '/catalog/product',
        optionLabel: 'name',
        value: 'id'
      },
      {
        type: 'select',
        label: 'Type de mouvement',
        name: 'type',
        multiple: true,
        optionLabel: 'label',
        value: 'id',
        options: [
          { id: 'creditContent', label: 'Avoir' },
          { id: 'commandContent', label: 'Bon de commande' },
          { id: 'deliveryContent', label: 'Bon de livraison' },
          { id: 'providerCommandContent', label: 'Commande fournisseur' },
          { id: 'quotationContent', label: 'Devis' },
          { id: 'invoiceContent', label: 'Facture' },
          { id: 'productLot', label: 'Lot' },
          { id: 'stockManualContent', label: 'Mouvement de stock manuel' },
          { id: 'product', label: 'Produit' },
          { id: 'productVariation', label: 'Variation de produit' },
        ]
      }
    ]

    if (this.hasModule('stock-locations')) {
      filters.push({
        type: 'select',
        name: 'stock_location',
        label: 'Lieu de stock',
        options: this.stockLocations,
        optionLabel: 'name',
        value: 'id',
        multiple: true
      })
    }

    return filters
  }

  refresh (): void {
    const table = this.$refs.table as Table
    table.refresh()
  }

  private productCreated (product: KwixeoCatalog.Product): void {
    if (product.with_stock) {
      this.refresh()
    }
  }

  private productUpdated (event: KwixeoCatalog.ProductUpdateEvent): void {
    const table = this.$refs.table as Table
    const index = table.getDatas().findIndex(data => data.id === event.new.id)
    if (index >= 0) {
      this.refresh()
    }
  }

  protected editManualMovement (movement: KwixeoCatalog.StockManualMovement): void {
    this.openModal({
      key: 'stock-manual-form',
      value: movement
    })
  }
}
